import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";


export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.must_change_password) {
        toast.info("Please set a new password to continue.");
        navigate("/change-password");
        return;
      }
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-[0.08]" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
            <img src="/rkf-logo.webp" alt="RKF" className="w-full h-full object-contain" data-testid="login-logo" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">My RKF</div>
            <div className="text-xs text-slate-400 uppercase tracking-widest">Project + Finance</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-tight">
            From MoU to<br/>Utilization Certificate<span className="text-blue-400">.</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-md text-sm leading-relaxed">
            Extract activities & milestones from your Scope of Work with AI. Track budgets across heads, run a compliant expense workflow, and export monthly, quarterly and annual UC reports.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[
              ["AI", "MoU Extraction"],
              ["3-tier", "Approval Flow"],
              ["PDF", "Draft UC"],
            ].map(([k, v]) => (
              <div key={v} className="border border-slate-800 rounded-lg p-3">
                <div className="font-mono text-xs text-blue-400">{k}</div>
                <div className="text-xs mt-1 text-slate-300">{v}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">v1.0 · Compliance-first</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <h2 className="font-display text-2xl font-bold">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">Use your organization credentials</p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0F172A] hover:bg-slate-800" data-testid="login-submit-btn">
                {loading ? "Signing in..." : "Sign in"}
              </Button>
              <div className="text-center">
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <p className="text-xs text-slate-500 text-center">
                No account? <Link to="/register" className="text-blue-600 font-semibold" data-testid="register-link">Create one</Link> · <Link to="/" className="text-slate-500 hover:text-slate-700">Home</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
